import { Link } from '@tanstack/react-router';
import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { Truncate } from 'cpool-components/general/Truncate';
import { useTranslation } from 'react-i18next';

import type { NotificationLinkOptions } from '~/features/notifications/utils/getNotificationLinkOptions';
import { dateFormat, timeFormat } from '~/lib/i18n';

type Props = {
    previewName: string;
    previewDetail: string;
    /** When provided, the detail is rendered inside a tooltip listing all items as bullet points. */
    tooltipItems?: ReadonlyArray<{ id: string; content: string }>;
    timestamp: Date | number | string | null | undefined;
    link: NotificationLinkOptions;
    onNavigate?: () => void;
};

/** A single clickable notification row. The category heading is rendered once by {@link NotificationGroup}. */
export const NotificationItemRow: React.FC<Props> = ({
    link,
    onNavigate,
    previewDetail,
    previewName,
    timestamp,
    tooltipItems,
}) => {
    const { t } = useTranslation();
    const date = timestamp ? new Date(timestamp) : null;
    const formattedDate = date ? `${dateFormat(date)} - ${timeFormat(date)} ${t('TOP_HEADER.NOTIFICATIONS.DATE')}` : '';

    const previewLine = (
        <Truncate maxWidth="100%" className="text-sm text-midnight">
            {previewDetail ? `${previewName} – ${previewDetail}` : previewName}
        </Truncate>
    );

    return (
        <Link {...link} onClick={onNavigate} className="block rounded-md hover:bg-surface-light px-2 -mx-2 py-1.5">
            <div className="flex flex-col gap-1.5">
                {tooltipItems && tooltipItems.length > 0 ? (
                    <Tooltip>
                        <TooltipTrigger asChild>{previewLine}</TooltipTrigger>
                        <TooltipContent side="top" sideOffset={6}>
                            <ul className="flex flex-col gap-1 list-disc pl-4">
                                {tooltipItems.map((item) => (
                                    <li key={item.id}>{item.content}</li>
                                ))}
                            </ul>
                        </TooltipContent>
                    </Tooltip>
                ) : (
                    previewLine
                )}
                {formattedDate && <span className="text-xs text-midnight-placeholder">{formattedDate}</span>}
            </div>
        </Link>
    );
};
